import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { FormControl } from '@mui/material';
import React from 'react';
import { useCommonFormUiTranslation } from '../hooks';
import { TimePicker, formatTime } from '../TimePicker';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { type } from './types';
const WidgetFormTimePicker = (props) => {
    var _a, _b, _c, _d, _e, _f;
    const value = {
        time: props.getDataNumberOrUndefined(),
    };
    const error = getVisibleError(props);
    const tabIndex = props.editMode ? -1 : undefined;
    const formLabelSuffix = getFormLabelSuffix(props);
    const label = formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label;
    const readOnly = props.config.readOnly || ((_a = props.rootParams.formParams) === null || _a === void 0 ? void 0 : _a.readOnly);
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, required: (_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.required, error: !!error, disabled: props.config.disabled },
        React.createElement(TimePicker, { label: label, minTime: (_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.minTime, maxTime: (_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.maxTime, timeFormat: props.config.timeFormat, timeMinuteStep: props.config.timeMinuteStep, required: (_e = props.config.validate) === null || _e === void 0 ? void 0 : _e.required, disabled: props.config.disabled, readOnly: readOnly, fullWidth: true, error: !!error, tabIndex: tabIndex, value: value, onChange: (newValue) => {
                if (readOnly) {
                    return;
                }
                props.setDataNumberOrUndefined(newValue.time);
                renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
            }, autoFocus: (_f = (!props.editMode && props.config.autoFocus)) !== null && _f !== void 0 ? _f : false, ariaDescribedBy: ariaDescribedBy }),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormTimePickerEditor = (props) => {
    const { t } = useCommonFormUiTranslation();
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'Time Picker: ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            description: true,
            autoFocus: true,
            onLoad: (data) => {
                if (!data.timeFormat) {
                    data.timeFormat = '24h';
                }
                if (data.timeMinuteStep == null) {
                    data.timeMinuteStep = 30;
                }
                return data;
            },
            children: [
                {
                    type: 'form.radio',
                    key: 'timeFormat',
                    label: 'Time Format',
                    values: [
                        { value: '24h', label: '24h' },
                        { value: '12h', label: `12h ${t('widget.widgetTimePicker.usingAmPm')}` },
                    ],
                    style: {
                        inline: true,
                    },
                },
                {
                    type: 'form.radio',
                    key: 'timeMinuteStep',
                    label: t('widget.widgetTimePicker.stepOverMinutes'),
                    values: [
                        { value: 1, label: '1' },
                        { value: 5, label: '5' },
                        { value: 10, label: '10' },
                        { value: 15, label: '15' },
                        { value: 30, label: '30' },
                        { value: 60, label: '60' },
                    ],
                    style: {
                        inline: true,
                    },
                },
            ],
        }), buildValidateTab(props, {
            required: true,
            children: [
                {
                    type: 'form.panel',
                    children: [
                        {
                            type: 'form.timePicker',
                            key: 'validate.minTime',
                            label: t('widget.widgetTimePicker.minTime'),
                            timeMinuteStep: 1,
                            style: {
                                width: 400,
                            },
                        },
                        {
                            type: 'form.text',
                            key: 'validate.minTimeMessage',
                            label: t('widget.widgetTimePicker.minTimeMessage'),
                            emptyAsNull: true,
                        },
                    ],
                    style: {
                        layout: 'row',
                    },
                },
                {
                    type: 'form.panel',
                    children: [
                        {
                            type: 'form.timePicker',
                            key: 'validate.maxTime',
                            label: t('widget.widgetTimePicker.maxTime'),
                            timeMinuteStep: 1,
                            style: {
                                width: 400,
                            },
                        },
                        {
                            type: 'form.text',
                            key: 'validate.maxTimeMessage',
                            label: t('widget.widgetTimePicker.maxTimeMessage'),
                            emptyAsNull: true,
                        },
                    ],
                    style: {
                        layout: 'row',
                    },
                },
            ],
        }), buildHiddenTab(props, { t }), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormTimePicker, Object.assign({}, props))));
};
export const widgetFormTimePicker = {
    type,
    validate: (props) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getDataNumberOrUndefined();
        if (value != null) {
            if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.minTime) != null && value < props.config.validate.minTime) {
                return ((_c = (_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.minTimeMessage) !== null && _c !== void 0 ? _c : props.config.label + ' cannot be before ' + formatTime(props.config.validate.minTime, props.config.timeFormat));
            }
            if (((_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.maxTime) != null && value > props.config.validate.maxTime) {
                return ((_f = (_e = props.config.validate) === null || _e === void 0 ? void 0 : _e.maxTimeMessage) !== null && _f !== void 0 ? _f : props.config.label + ' cannot be after ' + formatTime(props.config.validate.maxTime, props.config.timeFormat));
            }
        }
        else if (((_g = props.config.validate) === null || _g === void 0 ? void 0 : _g.required) && value == null) {
            if (props.config.label) {
                return props.config.label + ' is required';
            }
            else {
                return (_h = props.config.validate.requiredMessage) !== null && _h !== void 0 ? _h : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        const value = props.getDataNumberOrUndefined();
        if (value != null) {
            return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), formatTime(value, props.config.timeFormat));
        }
        return [];
    },
    component: WidgetFormTimePicker,
    icon: React.createElement(CalendarMonthIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormTimePickerEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'timePicker', label: 'Time Picker' }),
};
